import React, { useMemo, useState } from 'react';
import { galleryImages, galleryCategories } from '@/data/romaniaHub';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const DestinationGallery: React.FC = () => {
  const [cat, setCat] = useState('All');
  const [lightbox, setLightbox] = useState<number | null>(null);

  const images = useMemo(
    () => (cat === 'All' ? galleryImages : galleryImages.filter((g) => g.category === cat)),
    [cat]
  );

  const close = () => setLightbox(null);
  const prev = () => setLightbox((i) => (i === null ? i : (i - 1 + images.length) % images.length));
  const next = () => setLightbox((i) => (i === null ? i : (i + 1) % images.length));

  return (
    <section id="gallery" className="py-24 lg:py-32 bg-[#1A3B5C]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-14 text-white">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Through the Lens
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light mb-6">Destination Gallery</h2>
          <p className="font-sans text-sm text-white/60 max-w-2xl mx-auto leading-relaxed">
            An immersive portrait of Romania — captured on location across its
            destinations. Filter by place to explore each one.
          </p>

        </div>

        <div className="flex flex-wrap justify-center gap-2.5 mb-12">
          {galleryCategories.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`font-sans text-[10px] tracking-[0.15em] uppercase px-4 py-2 border transition-all ${
                cat === c
                  ? 'bg-[#C9A961] border-[#C9A961] text-white'
                  : 'border-white/20 text-white/70 hover:border-[#C9A961]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Masonry */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {images.map((img, i) => (
            <button
              key={img.src + i}
              onClick={() => setLightbox(i)}
              className="group relative mb-5 block w-full overflow-hidden break-inside-avoid"
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-[#1A3B5C]/0 group-hover:bg-[#1A3B5C]/40 transition-colors flex items-end p-4">
                <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  {img.category}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6"
          onClick={close}
        >
          <button onClick={close} className="absolute top-6 right-6 text-white/70 hover:text-white">
            <X className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-10 text-white/70 hover:text-white"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <figure className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={images[lightbox].src} alt={images[lightbox].alt} className="max-h-[80vh] w-auto mx-auto object-contain" />
            <figcaption className="text-center font-sans text-xs text-white/60 mt-4 tracking-wide">
              {images[lightbox].alt}
            </figcaption>
          </figure>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-10 text-white/70 hover:text-white"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
        </div>
      )}
    </section>
  );
};

export default DestinationGallery;
